import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { JsonDateParser } from "../../../../../../../react-core/Utils/JsonDateParser";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { ApplyModel } from "../../../../../../../Wrap/Models/Models/Processes/Apply/ApplyModel";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { ContributionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/ContributionBase";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { ChargeFromMap } from "../../AdviserCharges/Resource/Contents";
import { LessThanAge } from "../../Helpers/PensionHelper";
import { FrequencyArraysMap, FrequencyTypeArrays, SourceTypeMap } from "../../Resource/Contents";
import { CrystallizationPerContribution } from "../../SIPP/DrawdownDetails/CrystallizationPerContribution";
import {
    isCappedOrFlexIOrUFPLS,
    isRegualr,
    showBenefitTable,
} from "../../SIPP/DrawdownDetails/Helper/DrawdownDetailHelper";
import {
    CrystallisationOptionsMap,
    DrawdownTypeOptionsMap,
    LTADetailHeading,
    PensionOptionMap,
} from "../../SIPP/DrawdownDetails/Resource/Contents";
import { bankAccountParseWithHolderName, boolParse, dateParser, prepareTypography } from "../ReviewParser";

interface IDrawdownReviewProps {
    data: PensionBase;
    process: ProcessType;
    colNum: number;
    onEditAction: () => void;
    isOpen?: boolean;
    showDrawdownPriceLock?: boolean;
    showAssetsToSell?: boolean;
    modelData: ContributionBase | ApplyModel;
    isImmediateLumpSum: boolean;
    hasEnhancedPensionProtection: boolean;
}

export const DrawdownReview = (props: IDrawdownReviewProps) => {
    const { data, process, modelData, hasEnhancedPensionProtection } = props;
    const lessThan75 = LessThanAge(75, JsonDateParser.toDate(modelData.ClientDetails?.DateOfBirth));
    const drawdownConfig = () => {
        if (!data) {return [];}
        const drawdown = [];
        if (process == ProcessType.TakePensionBenefits) {
            drawdown.push(
                {
                    title: "Did you give financial advice?",
                    data: boolParse(data?.AdviceGiven),
                },
                {
                    title:
                        "Does your client have pension, drawdown or annuity that started before 6 April 2006 that you have not informed us of previously?",
                    data: boolParse(data?.DrawdownModel?.PreADay),
                },
                {
                    title: "Pension option",
                    data: PensionOptionMap[data?.DrawdownModel?.PensionOption],
                }
            );
        }
        if (data?.DrawdownModel?.PensionOption != PensionOption.UFPLS) {
            drawdown.push({
                title: "Drawdown type",
                data: DrawdownTypeOptionsMap[data?.DrawdownModel?.DrawdownType],
            });
        }
        return drawdown;
    };

    const crystallisationDetailConfig = () => {
        if (!data) {return [];}
        const crystallisationDetails = data?.DrawdownModel?.CrystallisationDetails;
        return [
            {
                title: <strong>Crystallisation details</strong>,
                data: " ",
            },
            {
                title: "Uncrystallised value",
                data: MoneyDisplayHelper.getMoneyFormat(crystallisationDetails?.UnCrystallisedValue),
            },
            {
                title: "Value available to crystallise",
                data: MoneyDisplayHelper.getMoneyFormat(crystallisationDetails?.AvailableToCrystallise),
            },
            {
                title: "Available cash",
                data: MoneyDisplayHelper.getMoneyFormat(crystallisationDetails?.AvailableCash),
            },
            {
                title: "Crystallisation options",
                data: CrystallisationOptionsMap[crystallisationDetails?.CrystallisationOption],
            },
            {
                title: "Crystallisation amount",
                data: MoneyDisplayHelper.getMoneyFormat(crystallisationDetails?.CrystallisationAmount),
            },
        ];
    };

    const regularCrystallisationDetailConfig = () => {
        if (!data) {return [];}
        const DrawdownModel = data?.DrawdownModel;
        return [
            {
                title: <strong>Regular crystallisation details</strong>,
                data: " ",
            },
            {
                title: "Regular crystallisation amount",
                data: MoneyDisplayHelper.getMoneyFormat(DrawdownModel?.RegularCrystallisationDetails?.Amount),
            },
            {
                title: "Specify a different initial crystallisation amount",
                data: boolParse(DrawdownModel?.RegularCrystallisationDetails?.HasADifferentInitialAmount),
            },
            {
                title: "Initial regular Crystallisation amount",
                data: DrawdownModel?.RegularCrystallisationDetails?.HasADifferentInitialAmount
                    ? MoneyDisplayHelper.getMoneyFormat(DrawdownModel?.RegularCrystallisationDetails?.InitialAmount)
                    : null,
            },
            {
                title: "Payment frequency",
                data: FrequencyArraysMap[DrawdownModel?.RegularCrystallisationDetails?.Frequency],
            },
            {
                title: "First payment date",
                data: dateParser(DrawdownModel?.RegularCrystallisationDetails?.FirstPaymentDay),
            },
        ];
    };

    const ltaBreakdownDetailsConfig = () => {
        if (!data) {return [];}
        const DrawdownModel = data?.DrawdownModel;
        const LifetimeAllowance = DrawdownModel?.LifetimeAllowance;
        const LTADetails = [];
        LTADetails.push({
            title: <strong>LTA breakdown details</strong>,
            data: " ",
        });
        if (lessThan75) {
            LTADetails.push(
                { title: LTADetailHeading.previousLTA, data: LifetimeAllowance?.SlaPreviouslyCrystallised + "%" },
                {
                    title: LTADetailHeading.LTABefore2006,
                    data: LifetimeAllowance?.SlaBenefitsPriorTo2006 + "%",
                }
            );
        }
        LTADetails.push({
            title: LTADetailHeading.otherLTA,
            data: DrawdownModel?.SlaUsedOffPlatform + "%",
        });
        if (lessThan75) {
            LTADetails.push({
                title: LTADetailHeading.availableLTA,
                data: MoneyDisplayHelper.getMoneyFormat(LifetimeAllowance?.AvailablePersonalAllowance),
            });
        }
        if (isCappedOrFlexIOrUFPLS(DrawdownModel) && lessThan75) {
            LTADetails.push(
                {
                    title: LTADetailHeading.LTAByThisCrystallisation,
                    data: LifetimeAllowance?.SlaUsedByProposedCrystallisation + "%",
                },
                {
                    title: LTADetailHeading.LTAInTotal,
                    data: LifetimeAllowance?.TotalSlaUsed + "%",
                },
                {
                    title: LTADetailHeading.crystallisationAmountSubjectToLTAExcessCharge,
                    data: MoneyDisplayHelper.getMoneyFormat(LifetimeAllowance?.ChargeableCrystallisationPortion),
                }
            );
        }
        if (isRegualr(DrawdownModel) && lessThan75) {
            LTADetails.push({ title: LTADetailHeading.estimateLTAForInitialCrystallisation, data: "0.00%" });
            LTADetails.push(
                { title: LTADetailHeading.estimateLTAPerRegularCrystallisation, data: "0.00%" },
                { title: LTADetailHeading.estimateAvailableLTAInAYear, data: "0.00%" },
                { title: LTADetailHeading.estimateLTAUsedInAYear, data: "0.00%" },
                { title: LTADetailHeading.estimateTotalLTAUsedInAYear, data: "0.00%" },
                {
                    title: LTADetailHeading.estimatedChargeableCrystallisation,
                    data: "2.00%",
                }
            );
        }
        return LTADetails;
    };

    const taxFreeLumpSumPaymentConfig = () => {
        if (!data) {return [];}
        const DrawdownModel = data?.DrawdownModel;
        const taxFreeLumpSum = [];
        taxFreeLumpSum.push({
            title: <strong>TFLS payment</strong>,
            data: " ",
        });
        taxFreeLumpSum.push({
            title: "Tax free lump sum payment",
            data: MoneyDisplayHelper.getMoneyFormat(DrawdownModel?.CurrentPclsEntitlement),
        });
        if (showBenefitTable(DrawdownModel, lessThan75, hasEnhancedPensionProtection)) {
            taxFreeLumpSum.push({
                title: "Gross excess benefits - Lump sum",
                data: DrawdownModel?.GrossExcessBenefitLumpSum + "%",
            });
            taxFreeLumpSum.push({
                title: "Gross excess benefits - Provide additional income",
                data: DrawdownModel?.GrossExcessBenefitProvideAdditionalIncome + "%",
            });
        }
        return taxFreeLumpSum;
    };

    const adviserChargesConfig = () => {
        if (!data) {return [];}
        const DrawdownModel = data?.DrawdownModel;
        const adviserCharges = [];
        adviserCharges.push({
            title: <strong>Adviser charges</strong>,
            data: " ",
        });
        if (
            DrawdownModel?.DrawdownType != DrawdownType.Regular &&
            DrawdownModel?.PensionOption != PensionOption.UFPLS
        ) {
            adviserCharges.push({
                title: "Rate",
                data:
                    DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Type ===
                    MoneyOrPercentageType.Money
                        ? MoneyDisplayHelper.getMoneyFormat(
                              DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money
                          )
                        : DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage + "%",
            });
            adviserCharges.push({
                title: "Take the charge from - DIAC",
                data: SourceTypeMap[DrawdownModel?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.SourceType],
            });
        }
        adviserCharges.push({
            title: "Annual rate",
            data: DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge
                ? DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Type == MoneyOrPercentageType.Money
                    ? MoneyDisplayHelper.getMoneyFormat(
                          DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Money
                      )
                    : DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Percentage + "%"
                : null,
        });
        adviserCharges.push({
            title: "Deduction frequency",
            data: FrequencyArraysMap[DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.Frequency],
        });
        adviserCharges.push({
            title: "Take the charge from - OAC",
            data: ChargeFromMap[DrawdownModel?.DrawdownAdviserCharge?.OngoingAdviserCharge?.SourceType],
        });
        return adviserCharges;
    };

    const drawdownPriceLockConfig = () => {
        if (!data) {return [];}
        const DrawdownModel = data?.DrawdownModel;
        const drawdownPriceLock = [];
        drawdownPriceLock.push({
            title: <strong>Drawdown price lock</strong>,
            data: " ",
        });
        drawdownPriceLock.push({
            title: "Do you want to apply the Drawdown Price Lock?",
            data: boolParse(DrawdownModel?.DrawdownPriceLock),
        });
        if (DrawdownModel?.DrawdownPriceLock) {
            drawdownPriceLock.push({
                title: "Drawdown price lock",
                data: DrawdownModel?.DrawdownPriceLockPercentage,
            });
        }
        return drawdownPriceLock;
    };

    const assetsToSellConfig = () => {
        if (!data) {return [];}
        const DrawdownModel = data?.DrawdownModel;
        const assetsToSell = [];
        assetsToSell.push({
            title: <strong>Choose assets to sell</strong>,
            data: " ",
        });
        assetsToSell.push({
            title: "Choose asset selection method",
            data: DrawdownModel?.IsAcrossAllAssets ? "Proportionally across all assets" : "From specified assets",
        });
        if (!DrawdownModel?.IsAcrossAllAssets) {
            for (const el of DrawdownModel?.PensionCommencementLumpSumAllocations) {
                const name = data?.PreRetirementInstrumentSelections?.Instruments?.find((selection) => {
                    return selection?.Instrument.ProductId === el.ProductId;
                }).Instrument.Name;
                assetsToSell.push({
                    title: <div className="pl-10">{name}</div>,
                    data: <div className="pl-10">{el?.Percentage}%</div>,
                });
            }
        }
        return assetsToSell;
    };
    const bankAccountDetailsConfig = () => {
        if (!data) {return [];}
        const { IncomePaymentAllocations } = data?.DrawdownModel?.Income;
        if (!IncomePaymentAllocations) {return [];}
        const bankDetails = [];
        bankDetails.push({
            title: <strong>Bank account details</strong>,
            data: " ",
        });
        IncomePaymentAllocations.map((bankAccount) => {
            bankDetails.push({
                title: "Bank account",
                data: bankAccountParseWithHolderName(
                    bankAccount.BankAccountId,
                    modelData?.AllBankAccounts || [],
                    bankAccount.Destination
                ),
            });
            bankDetails.push({
                title: "Portion of PCLS",
                data: bankAccount.PclsAllocation + "%",
            });
            bankDetails.push({
                title: "Proportion of income",
                data: bankAccount.Allocation + "%",
            });
        });
        return bankDetails;
    };

    const title = process == ProcessType.TakePensionBenefits ? "Pension benefit details" : "Drawdown details";
    return (
        <Accordion label={title} isOpen={false} onEdit={props.onEditAction}>
            <div className="pl-34">
                <Typography {...prepareTypography(drawdownConfig(), 1, "common")} />
                <br />
                {data?.DrawdownModel?.DrawdownType === DrawdownType.Regular &&
                data?.DrawdownModel?.PensionOption != PensionOption.UFPLS ? (
                    <Typography {...prepareTypography(regularCrystallisationDetailConfig(), 1, "common")}></Typography>
                ) : (
                    <Typography {...prepareTypography(crystallisationDetailConfig(), 1, "common")}></Typography>
                )}
                <br />
                <Typography {...prepareTypography(ltaBreakdownDetailsConfig(), 1, "common")}></Typography>
                <br />
                <Typography {...prepareTypography(taxFreeLumpSumPaymentConfig(), 1, "common")}></Typography>
                <br />
                {props.isImmediateLumpSum && (
                    <CrystallizationPerContribution
                        data={data?.DrawdownModel}
                        onChange={() => {}}
                        onRef={() => {}}
                        modelData={data}
                    />
                )}
                <Typography {...prepareTypography(adviserChargesConfig(), 1, "common")}></Typography>
                {props.showDrawdownPriceLock && (
                    <Typography {...prepareTypography(drawdownPriceLockConfig(), 1, "common")} />
                )}
                <br />
                {props.showAssetsToSell && (
                    <Typography {...prepareTypography(assetsToSellConfig(), 1, "common")}></Typography>
                )}
                {data?.DrawdownModel?.PensionOption == PensionOption.UFPLS && (
                    <Typography {...prepareTypography(bankAccountDetailsConfig(), 1, "common")} />
                )}
            </div>
        </Accordion>
    );
};
